import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { usePageTitle } from '@/hooks/usePageTitle'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Logo } from '@/components/logo'
import {
  Loader2,
  AlertCircle,
  CheckCircle,
  Users,
  Building,
  Mail,
  Clock,
  UserPlus
} from 'lucide-react'
import { PageTransition } from '@/components/PageTransition'

const AcceptInvitationPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, userProfile, company: userCompany, signOut, loading: authLoading } = useAuth()
  usePageTitle('Accept Invitation')

  const [invitation, setInvitation] = useState(null)
  const [company, setCompany] = useState(null)
  const [inviter, setInviter] = useState(null)
  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)
  const [error, setError] = useState('')
  const [needsSignup, setNeedsSignup] = useState(false)
  const [userHasProfileForCompany, setUserHasProfileForCompany] = useState(false)

  // Form state for signup
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')

  const token = searchParams.get('token')

  // Load invitation details
  useEffect(() => {
    const loadInvitation = async () => {
      console.log('Loading invitation, token:', token)

      if (!token) {
        console.log('No token provided')
        setError('Invalid invitation link')
        setLoading(false)
        return
      }

      try {
        // Get invitation details
        console.log('Fetching invitation from database...')
        const { data: inviteData, error: inviteError } = await supabase
          .from('company_invitations')
          .select(`
            *,
            company:companies(*),
            inviter:user_profiles!invited_by(display_name, email)
          `)
          .eq('invitation_token', token)
          .single()

        console.log('Invitation query result:', { inviteData, inviteError })

        if (inviteError || !inviteData) {
          console.log('Invitation not found or error')
          setError('Invalid or expired invitation')
          setLoading(false)
          return
        }
        
        // Check invitation status
        if (inviteData.status !== 'pending') {
          setError(`This invitation has already been ${inviteData.status}`)
          setLoading(false)
          return
        }
        
        // Check expiration
        if (new Date(inviteData.expires_at) < new Date()) {
          setError('This invitation has expired')
          // Update status in database
          await supabase
            .from('company_invitations')
            .update({ status: 'expired' })
            .eq('id', inviteData.id)
          setLoading(false)
          return
        }
        
        setInvitation(inviteData)
        setCompany(inviteData.company)
        setInviter(inviteData.inviter)
        setEmail(inviteData.email)

        // Check if user needs to sign up
        if (!user) {
          // Check if email already has an authenticated account (not just a profile)
          const { data: existingUser } = await supabase
            .from('user_profiles')
            .select('id, auth_id')
            .eq('email', inviteData.email)
            .maybeSingle()

          console.log('Existing user check:', existingUser)

          // User needs to sign up if no profile exists OR if profile exists but has no auth_id
          setNeedsSignup(!existingUser || !existingUser.auth_id)
        } else {
          // Check if logged-in user already has a profile for THIS specific company
          const { data: existingProfile } = await supabase
            .from('user_profiles')
            .select('id')
            .eq('auth_id', user.id)
            .eq('company_id', inviteData.company_id)
            .maybeSingle()

          setUserHasProfileForCompany(!!existingProfile)
        }

        setLoading(false)
      } catch (err) {
        console.error('Error loading invitation:', err)
        setError('Failed to load invitation details')
        setLoading(false)
      }
    }
    
    if (!authLoading) {
      loadInvitation()
    }
  }, [token, user, authLoading])

  // Handle accepting invitation for existing users
  const handleAcceptInvitation = async () => {
    if (!user || !invitation) return

    setAccepting(true)
    setError('')

    try {
      // Get user's existing profile to copy personal info
      // Use .limit(1) instead of .maybeSingle() since user may have profiles in multiple companies
      const { data: existingProfiles, error: existingProfileError } = await supabase
        .from('user_profiles')
        .select('first_name, last_name, avatar_url, email')
        .eq('auth_id', user.id)
        .limit(1)

      if (existingProfileError) {
        throw new Error('Failed to fetch existing profile')
      }

      const existingProfile = existingProfiles?.[0] || null

      // Create a new user_profile for this company
      const { data: newProfile, error: createProfileError } = await supabase
        .from('user_profiles')
        .insert({
          auth_id: user.id,
          email: invitation.email,
          first_name: existingProfile?.first_name || '',
          last_name: existingProfile?.last_name || '',
          avatar_url: existingProfile?.avatar_url || null,
          company_id: invitation.company_id,
          role: invitation.role,
          display_name: existingProfile
            ? `${existingProfile.first_name} ${existingProfile.last_name}`.trim()
            : invitation.email.split('@')[0]
        })
        .select()
        .single()

      if (createProfileError) throw createProfileError

      // Add to company_members
      const { error: memberError } = await supabase
        .from('company_members')
        .insert({
          company_id: invitation.company_id,
          user_id: newProfile.id,
          role: invitation.role,
          permission_group_id: invitation.permission_group_id,
          invited_via: invitation.id
        })

      if (memberError && memberError.code !== '23505') { // Ignore duplicate key error
        throw memberError
      }

      // Update invitation status
      const { error: inviteUpdateError } = await supabase
        .from('company_invitations')
        .update({
          status: 'accepted',
          accepted_at: new Date().toISOString(),
          accepted_by: newProfile.id
        })
        .eq('id', invitation.id)

      if (inviteUpdateError) throw inviteUpdateError

      // Reload page to trigger AuthContext to load new profiles
      window.location.href = '/dashboard'
    } catch (err) {
      console.error('Error accepting invitation:', err)
      setError('Failed to accept invitation. Please try again.')
      setAccepting(false)
    }
  }

  // Handle signup and accept invitation
  const handleSignupAndAccept = async (e) => {
    e.preventDefault()
    setAccepting(true)
    setError('')
    
    try {
      // Sign up the user
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: email,
        password: password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            display_name: `${firstName} ${lastName}`.trim(),
            company_id: invitation.company_id,
            invitation_id: invitation.id
          }
        }
      })
      
      if (signUpError) throw signUpError
      
      if (authData?.user) {
        // The trigger will handle:
        // 1. Creating the user profile
        // 2. Adding to company_members
        // 3. Updating invitation status
        // So we just need to wait a moment and navigate
        
        // Wait a moment for the trigger to complete
        await new Promise(resolve => setTimeout(resolve, 1500))
        
        // Show success message
        setError('')
        navigate('/dashboard')
      }
    } catch (err) {
      console.error('Error during signup:', err)
      setError(err.message || 'Failed to create account. Please try again.')
      setAccepting(false)
    }
  }

  // Loading state
  if (loading || authLoading) {
    return (
      <PageTransition>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading invitation...</p>
          </div>
        </div>
      </PageTransition>
    )
  }

  // Error state
  if (error && !invitation) {
    return (
      <PageTransition>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <CardTitle>Invalid Invitation</CardTitle>
              <CardDescription>{error}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                className="w-full"
                onClick={() => navigate('/login')}
              >
                Go to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </PageTransition>
    )
  }

  // Main invitation acceptance UI
  return (
    <PageTransition>
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <Logo className="mx-auto mb-4" />
          {!user || !userHasProfileForCompany ? (
            <>
              <CardTitle className="text-2xl">Join {company?.name}</CardTitle>
              <CardDescription>
                You've been invited to join as {invitation?.role === 'admin' ? 'an' : 'a'} <Badge variant="secondary">{invitation?.role}</Badge>
              </CardDescription>
            </>
          ) : (
            <>
              <CardTitle className="text-2xl">Already a Member</CardTitle>
              <CardDescription>
                You already have a profile for this company
              </CardDescription>
            </>
          )}
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Invitation Details - Only show if user is not logged in or doesn't have profile for this company */}
          {(!user || !userHasProfileForCompany) && (
            <>
              <div className="bg-muted p-4 rounded-lg space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Building className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">Company:</span>
                  <span>{company?.name}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <UserPlus className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">Invited by:</span>
                  <span>{inviter?.display_name || inviter?.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">Invitation for:</span>
                  <span>{invitation?.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">Expires:</span>
                  <span>{new Date(invitation?.expires_at).toLocaleDateString()}</span>
                </div>
              </div>

              {/* Personal Message */}
              {invitation?.message && (
                <Alert>
                  <AlertDescription>
                    <p className="font-medium mb-1">Message from {inviter?.display_name}:</p>
                    <p className="text-sm italic">"{invitation.message}"</p>
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Action based on user state */}
          {user ? (
            userHasProfileForCompany ? (
              // User already has a profile for this specific company
              <div className="space-y-4">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <p className="font-medium mb-2">You're already a member of this company</p>
                    <p className="text-sm">
                      You already have a profile for {company?.name}. This invitation cannot be accepted again.
                    </p>
                  </AlertDescription>
                </Alert>

                <Alert>
                  <AlertDescription className="text-sm">
                    <p className="mb-1">Currently logged in as: <strong>{user.email}</strong></p>
                  </AlertDescription>
                </Alert>

                <Button
                  className="w-full"
                  onClick={() => navigate('/dashboard')}
                >
                  Go to Dashboard
                </Button>
              </div>
            ) : (
              // User is logged in but doesn't have a profile for this company - can accept and create new profile
              <div className="space-y-4">
                <Alert>
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription>
                    You're logged in as <strong>{user.email}</strong>
                    {userProfile && userCompany && (
                      <p className="mt-1 text-sm">
                        A new profile will be created for {company?.name}. You can switch between your profiles using the account switcher.
                      </p>
                    )}
                  </AlertDescription>
                </Alert>

                <Button
                  className="w-full bg-green-600 hover:bg-green-700"
                  onClick={handleAcceptInvitation}
                  disabled={accepting}
                >
                  {accepting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Accepting Invitation...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Accept Invitation
                    </>
                  )}
                </Button>
              </div>
            )
          ) : needsSignup ? (
            // User needs to sign up
            <form onSubmit={handleSignupAndAccept} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  disabled
                  className="bg-muted"
                />
              </div>
              
              <div>
                <Label htmlFor="password">Choose Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  placeholder="At least 8 characters"
                />
              </div>
              
              <Button 
                type="submit"
                className="w-full bg-green-600 hover:bg-green-700"
                disabled={accepting}
              >
                {accepting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Create Account & Join
                  </>
                )}
              </Button>
            </form>
          ) : (
            // User has account but needs to log in
            <div className="space-y-4">
              <Alert>
                <AlertDescription>
                  <p className="font-medium mb-2">An account with this email already exists</p>
                  <p className="text-sm">Please log in to accept the invitation. If you don't remember your password, you can reset it.</p>
                </AlertDescription>
              </Alert>

              <Button
                className="w-full"
                onClick={() => navigate(`/login?redirect=${encodeURIComponent(`/accept-invitation?token=${token}`)}`)}
              >
                Log In to Accept
              </Button>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/forgot-password')}
              >
                Forgot Password?
              </Button>

              <div className="text-center text-sm text-muted-foreground">
                <p>Don't recognize this account?</p>
                <p className="text-xs mt-1">Contact your administrator for assistance</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </PageTransition>
  )
}

export default AcceptInvitationPage